// src/components/initial-inventory/SessionSummary.tsx
// Session summary component showing items added during current initial inventory session
// Displays running tally grouped by category

import React from 'react';
import { Package, RefreshCw, CheckCircle, Clock } from 'lucide-react';
import { SessionSummaryResponse } from '../../services/initialInventoryAPI';
import { CategoryHierarchy } from '../../types/category';

interface SessionSummaryProps {
  summary: SessionSummaryResponse | null;
  categories: CategoryHierarchy[];
  onRefresh: () => void;
}

const SessionSummary: React.FC<SessionSummaryProps> = ({
  summary,
  categories,
  onRefresh
}) => {
  const getCategoryPath = (categoryId: string): string => {
    const category = categories.find(c => c.categoryId === categoryId);
    if (!category) return categoryId;
    
    if (category.parentCategoryId) {
      const parent = categories.find(c => c.categoryId === category.parentCategoryId);
      return parent ? `${parent.name} › ${category.name}` : category.name;
    }
    
    return category.name;
  };

  const formatTime = (isoString: string): string => {
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Empty state
  if (!summary || summary.totalItems === 0) {
    return (
      <div className="text-center py-8">
        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <Package className="w-6 h-6 text-gray-400" />
        </div>
        <p className="text-gray-500 text-sm mb-1">No items added yet</p>
        <p className="text-gray-400 text-xs">
          Items you add will appear here
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Stats */}
      <div className="bg-green-50 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
            <span className="text-green-800 font-medium">
              {summary.totalItems} item{summary.totalItems !== 1 ? 's' : ''} added
            </span>
          </div>
          <button
            onClick={onRefresh}
            className="p-1.5 hover:bg-green-100 rounded transition-colors"
            title="Refresh summary"
          >
            <RefreshCw className="w-4 h-4 text-green-600" />
          </button>
        </div>
        
        {summary.totalQuantity !== summary.totalItems && (
          <p className="text-green-700 text-sm mt-1">
            Total quantity: {summary.totalQuantity.toLocaleString()}
          </p>
        )}
        
        <div className="flex items-center text-xs text-green-600 mt-2">
          <Clock className="w-3 h-3 mr-1" />
          Session started {formatTime(summary.since)}
        </div>
      </div>

      {/* Items by Category */}
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-gray-700">By Category</h3>
        
        {summary.byCategory.map((category, idx) => (
          <div 
            key={`${category.categoryId}-${idx}`}
            className="border border-gray-200 rounded-lg overflow-hidden"
          >
            {/* Category Header */}
            <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
              <div className="text-sm font-medium text-gray-700 truncate">
                {getCategoryPath(category.subcategoryId || category.categoryId)}
              </div>
              <div className="text-xs text-gray-500">
                {category.totalCount} item{category.totalCount !== 1 ? 's' : ''}
              </div>
            </div>
            
            {/* Items List */}
            <div className="divide-y divide-gray-100 max-h-48 overflow-y-auto">
              {category.items.slice(0, 10).map((item, itemIdx) => (
                <div 
                  key={`${item.itemId}-${itemIdx}`}
                  className="px-3 py-2 text-sm flex items-center justify-between"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-gray-900 truncate">
                      {item.name}
                    </div>
                    {item.serialNumber && (
                      <div className="text-xs text-gray-500 font-mono">
                        S/N: {item.serialNumber}
                      </div>
                    )}
                  </div>
                  <div className="flex-shrink-0 ml-2">
                    {item.quantity > 1 ? (
                      <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                        ×{item.quantity}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">
                        {item.itemType === 'SERIALIZED' ? '1 unit' : `${item.quantity}`}
                      </span>
                    )}
                  </div>
                </div>
              ))}
              
              {category.items.length > 10 && (
                <div className="px-3 py-2 text-xs text-gray-500 text-center bg-gray-50">
                  +{category.items.length - 10} more items
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Session Note */}
      <p className="text-xs text-gray-400 text-center">
        Session expires in 24 hours
      </p>
    </div>
  );
};

export default SessionSummary;
